/**
 * What the editor reports under the document: its findings (preview's
 * live lint, and the form's own refusals), the state of the check, and
 * Save's outcome: "Opened !N" / "Pushed to !N" with the MR's link, or the
 * refusal as the backend wrote it (a 409 with an Open in GitLab link).
 */

import type { ReactElement } from 'react';
import { panelErrorMessage, saveErrorDetail } from '../utils/errors';
import type { LintFinding, SaveResponse } from '../types/projectPanel.types';

export function EditorFindings({ findings }: { findings: LintFinding[] }): ReactElement | null {
  if (findings.length === 0) return null;
  return (
    <ul className="def-findings def-editor__findings" aria-label="Findings">
      {findings.map((f) => (
        <li key={`${f.severity}:${f.path}:${f.message}`} className={`def-findings__item def-findings__item--${f.severity}`}>
          {f.severity === 'error' ? '✗' : '⚠'} {f.message}
          {f.path !== '' && <span className="def-editor__finding-path"> ({f.path})</span>}
        </li>
      ))}
    </ul>
  );
}

/** The MR a 409 names (`!N`), else the project's MRs. */
function gitlabLink(message: string, webUrl: string | null): string | null {
  if (webUrl === null) return null;
  const iid = /!(\d+)/.exec(message)?.[1];
  return iid === undefined ? `${webUrl}/-/merge_requests` : `${webUrl}/-/merge_requests/${iid}`;
}

interface SaveOutcomeProps {
  result: SaveResponse | undefined;
  error: Error | null;
  projectWebUrl: string | null;
}

export function SaveOutcome({ result, error, projectWebUrl }: SaveOutcomeProps): ReactElement | null {
  if (result !== undefined) {
    return (
      <p className="def-editor__result" role="status">
        {result.created ? 'Opened' : 'Pushed to'}{' '}
        <a href={result.mr_url} target="_blank" rel="noreferrer" className="runs-page__link">
          !{result.mr_iid}
        </a>
      </p>
    );
  }
  if (error === null) return null;
  const detail = saveErrorDetail(error);
  const message = detail?.message ?? panelErrorMessage(error);
  const link = detail?.status === 409 ? gitlabLink(message, projectWebUrl) : null;
  return (
    <div className="error-message def-editor__result" role="alert">
      <span>{message}</span>
      {detail !== null && <EditorFindings findings={detail.findings} />}
      {link !== null && (
        <>
          {' '}
          <a href={link} target="_blank" rel="noreferrer" className="runs-page__link">
            Open in GitLab
          </a>
        </>
      )}
    </div>
  );
}
